/**
 * Choosing a password.
 *
 * One screen for three arrivals, because they are the same act and should not be three designs:
 *
 * - **A reset link** (`/reset-password?token=…`) — the token proves it, and the server signs them
 *   in once the password is set, so they are not bounced to a form to retype what they just chose.
 * - **A temporary password** just used — the session is already in the forced-reset state, and
 *   nothing else in the app is reachable until this is done.
 * - **An ordinary change**, from the profile screen, which asks for the current password.
 *
 * The confirm field is checked here rather than server-side on purpose: it is a typo check, not a
 * security control, and the person needs the answer while their hands are still on the keyboard.
 */

import { useState, type FormEvent } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button, Callout, Card, TextField } from "@powwow/ui";
import { fetchMe, setPassword, describeError } from "../api.js";

/** Matches `passwordSchema` in @powwow/validation — length, and nothing else. */
const MINIMUM = 12;

export function SetPasswordScreen() {
  const [params] = useSearchParams();
  const token = params.get("token") ?? undefined;
  const navigate = useNavigate();

  const [password, setValue] = useState("");
  const [confirm, setConfirm] = useState("");
  const [current, setCurrent] = useState("");
  const [needsCurrent, setNeedsCurrent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const tooShort = password.length > 0 && password.length < MINIMUM;
  const mismatch = confirm.length > 0 && confirm !== password;

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (password.length < MINIMUM || password !== confirm) return;
    setBusy(true);
    setError(null);
    try {
      await setPassword({
        password,
        ...(token ? { token } : {}),
        ...(needsCurrent && current ? { currentPassword: current } : {}),
      });
      const me = await fetchMe();
      navigate(
        me.authenticated && me.platformAdmin && me.communities.length === 0
          ? "/admin/communities"
          : "/",
        { replace: true },
      );
    } catch (cause) {
      const message = describeError(cause);
      // The server is the authority on whether the current password was needed; if it says so,
      // show the field rather than leaving someone stuck on a form that cannot succeed.
      if (message.toLowerCase().includes("current password")) setNeedsCurrent(true);
      setError(message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="grid min-h-dvh place-items-center px-4 py-10">
      <Card className="w-full max-w-md p-6">
        <h1 className="text-title font-black">Choose a password</h1>
        <p className="mt-1 text-muted">
          {token
            ? "Pick a new password for your account."
            : "You are signed in with a temporary password. Choose your own to continue."}
        </p>

        {error && (
          <Callout label="Problem" tone="danger" className="mt-4">
            {error}
          </Callout>
        )}

        <form onSubmit={submit} className="mt-5 space-y-4">
          {needsCurrent && (
            <TextField
              label="Current password"
              type="password"
              value={current}
              autoComplete="current-password"
              required
              onChange={(event) => setCurrent(event.target.value)}
            />
          )}
          <TextField
            label="New password"
            type="password"
            value={password}
            autoComplete="new-password"
            required
            hint={`At least ${MINIMUM} characters. A short phrase you will remember beats a jumble you will not.`}
            {...(tooShort ? { error: `Use at least ${MINIMUM} characters` } : {})}
            onChange={(event) => setValue(event.target.value)}
          />
          <TextField
            label="Confirm new password"
            type="password"
            value={confirm}
            autoComplete="new-password"
            required
            {...(mismatch ? { error: "These do not match" } : {})}
            onChange={(event) => setConfirm(event.target.value)}
          />
          <Button
            type="submit"
            busy={busy}
            disabled={password.length < MINIMUM || password !== confirm}
            className="w-full"
          >
            {busy ? "Saving…" : "Save password"}
          </Button>
        </form>
      </Card>
    </main>
  );
}
